import { ApolloLink, Observable } from '@apollo/client';

import auth from './Auth';

export enum NexoyaCustomHeader {
  SessionId = 'x-nexoya-session-id',
  Debug = 'x-nexoya-debug',
}

type AuthLinkOptions = { sessionId: string; sendDebugHeader: () => boolean };

// Same session test as before: an expired but still stored token counts as no session.
const getValidToken = (): string | null => {
  const token = auth.getAccessToken();
  return auth.isAuthenticated() && token && token.trim() ? token : null;
};

export function createAuthLink({ sessionId, sendDebugHeader }: AuthLinkOptions): ApolloLink {
  return new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        let closed = false;
        let inner: { unsubscribe: () => void } | undefined;

        const send = (token: string) => {
          operation.setContext(({ headers = {} }) => ({
            headers: {
              ...headers,
              authorization: `Bearer ${token}`,
              [NexoyaCustomHeader.SessionId]: sessionId,
              ...(sendDebugHeader() ? { [NexoyaCustomHeader.Debug]: 'true' } : {}),
            },
          }));
          inner = forward(operation).subscribe(observer);
        };

        const initial = getValidToken();
        if (initial) {
          send(initial);
        } else {
          // The token may be missing only while a silent restore is in flight.
          auth
            .restoreSession()
            .catch(() => false)
            .then(() => {
              if (closed) return;
              const token = getValidToken();
              if (token) return send(token);
              auth.login({ signup: false, customQueryParams: null });
              observer.complete();
            })
            .catch((e) => observer.error(e));
        }

        return () => {
          closed = true;
          inner?.unsubscribe();
        };
      }),
  );
}
